import Dexie from "dexie";
import {
  getCachePolicy,
  MAX_CACHED_RESPONSES_PER_USER,
  normalizeUrlPath,
  stableSerialize,
} from "./policies";
import {
  OPTIMISTIC_REFERENCE_RESOURCES,
  projectQueuedMutations,
} from "./optimisticData";
import {
  getOfflineEntityId,
  getUnresolvedOfflineReferences,
  replaceOfflineReferences,
} from "./offlineReferences";
import { getQueueRemovalImpact } from "./queueMetadata";

export const offlineDb = new Dexie("evivesoma-offline");

offlineDb.version(1).stores({
  responses: "&key, userId, updatedAt",
  queue: "++id, userId, status, createdAt",
});

offlineDb.version(2).stores({
  responses: "&key, userId, updatedAt, expiresAt",
  queue: "++id, &clientRequestId, userId, status, createdAt, nextAttemptAt",
});

offlineDb.version(3).stores({
  responses: "&key, userId, updatedAt, expiresAt",
  queue: "++id, &clientRequestId, userId, status, createdAt, nextAttemptAt",
  metadata: "&key, userId, module, updatedAt",
});

offlineDb.version(4).stores({
  responses: "&key, userId, updatedAt, expiresAt",
  queue: "++id, &clientRequestId, userId, status, createdAt, nextAttemptAt",
  metadata: "&key, userId, module, updatedAt",
  offlineAuth: "&key, module, username, updatedAt, expiresAt",
});

export const getCurrentUserId = () => {
  try {
    return JSON.parse(window.localStorage.getItem("user"))?.id || "anonymous";
  } catch {
    return "anonymous";
  }
};

const getCurrentContext = () => {
  const readId = (key) => {
    try {
      return JSON.parse(window.localStorage.getItem(key))?.id || "";
    } catch {
      return "";
    }
  };

  return {
    yearId: window.localStorage.getItem("yearId") || "",
    profileId: readId("profile"),
    establishmentId: readId("establishment"),
    module: window.sessionStorage.getItem("activeModule") || "",
  };
};

const createPreparationKey = () =>
  `${getCurrentUserId()}::${stableSerialize(getCurrentContext())}::preparation`;

export const createResponseKey = (config) => {
  const context = stableSerialize(getCurrentContext());
  const params = stableSerialize(config.params || {});
  const path = normalizeUrlPath(config.url);
  let query = "";

  try {
    const parsed = new URL(config.url || "", window.location.origin);
    query = stableSerialize(
      Array.from(parsed.searchParams.entries()).sort(
        ([leftKey, leftValue], [rightKey, rightValue]) =>
          leftKey === rightKey
            ? leftValue.localeCompare(rightValue)
            : leftKey.localeCompare(rightKey)
      )
    );
  } catch {
    query = stableSerialize([]);
  }

  return `${getCurrentUserId()}::${context}::${path}::${query}::${params}`;
};

const createLegacyResponseKeys = (config) => {
  const context = stableSerialize(getCurrentContext());
  const params = stableSerialize(config.params || {});
  const url = config.url || "";
  const variants = new Set([url]);

  if (url.startsWith("/")) variants.add(url.replace(/^\/+/, ""));
  else variants.add(`/${url}`);

  return Array.from(variants).map(
    (variant) =>
      `${getCurrentUserId()}::${context}::${config.baseURL || ""}${variant}::${params}`
  );
};

const pruneCachedResponses = async (userId) => {
  // Keep stale entries as a last-resort fallback when refreshed resources fail.
  await offlineDb.responses
    .where("userId")
    .equals(userId)
    .and(() => false)
    .delete();

  const entries = await offlineDb.responses
    .where("userId")
    .equals(userId)
    .sortBy("updatedAt");
  const overflow = entries.length - MAX_CACHED_RESPONSES_PER_USER;
  if (overflow > 0) {
    await offlineDb.responses.bulkDelete(entries.slice(0, overflow).map((entry) => entry.key));
  }
};

export const cacheResponse = async (config, data, status = 200) => {
  const userId = getCurrentUserId();
  const policy = getCachePolicy(config.url);
  const updatedAt = Date.now();

  await offlineDb.responses.put({
    key: createResponseKey(config),
    userId,
    data,
    status,
    policy: policy.name,
    updatedAt,
    expiresAt: updatedAt + policy.maxAge,
  });
  await pruneCachedResponses(userId);
};

export const getCachedResponse = async (config, { allowExpired = false } = {}) => {
  const key = createResponseKey(config);
  let cached = await offlineDb.responses.get(key);

  if (!cached) {
    for (const legacyKey of createLegacyResponseKeys(config)) {
      cached = await offlineDb.responses.get(legacyKey);
      if (cached) break;
    }
  }
  if (!cached) return undefined;

  if (!cached.expiresAt || cached.expiresAt <= Date.now()) {
    if (!allowExpired) {
      await offlineDb.responses.delete(key);
      return undefined;
    }
    return { ...cached, stale: true };
  }

  return cached;
};

const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
};

const parseQueuedData = (data) => {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const mergeQueuedData = (creationData, modificationData) => {
  const creation = parseQueuedData(creationData);
  if (modificationData === undefined) return creation;
  const modification = parseQueuedData(modificationData);

  if (
    !creation ||
    !modification ||
    Array.isArray(creation) ||
    Array.isArray(modification) ||
    typeof creation !== "object" ||
    typeof modification !== "object"
  ) {
    return modification;
  }

  const merged = Object.keys(modification).reduce(
    (result, key) => ({
      ...result,
      [key]: mergeQueuedData(creation[key], modification[key]),
    }),
    { ...creation }
  );

  // The API uses this field to distinguish a creation from an edit.
  if (Object.prototype.hasOwnProperty.call(creation, "operation")) {
    merged.operation = creation.operation;
  }

  return merged;
};

export const findQueuedCreationUpdate = (items, config, userId) => {
  const method = (config.method || "post").toLowerCase();
  if (!["put", "patch"].includes(method)) return null;

  const targetParts = normalizeUrlPath(config.url).split("/").filter(Boolean);
  if (targetParts.length !== 2) return null;
  const [resource, targetId] = targetParts;

  const creation = items.find(
    (item) =>
      item.userId === userId &&
      item.method === "post" &&
      item.status !== "syncing" &&
      normalizeUrlPath(item.url) === resource &&
      getOfflineEntityId(item) === targetId
  );
  if (!creation) return null;

  return {
    creation,
    changes: {
      data: mergeQueuedData(creation.data, config.data),
      params: mergeQueuedData(creation.params, config.params),
      status: "pending",
      attempts: 0,
      error: null,
      blockedReason: null,
      blockedReferences: [],
      nextAttemptAt: 0,
    },
  };
};

export const addQueuedRequest = async (config) => {
  const headers = config.headers || {};
  const clientRequestId = headers["X-Idempotency-Key"] || createRequestId();
  const userId = getCurrentUserId();
  const coalesced = await offlineDb.transaction("rw", offlineDb.queue, async () => {
    const items = await offlineDb.queue.where("userId").equals(userId).toArray();
    const update = findQueuedCreationUpdate(items, config, userId);
    if (!update) return null;

    await offlineDb.queue.update(update.creation.id, update.changes);
    return {
      ...update.creation,
      ...update.changes,
      coalesced: true,
    };
  });

  if (coalesced) {
    window.dispatchEvent(new CustomEvent("offline:queue-changed"));
    return coalesced;
  }

  const queued = {
    clientRequestId,
    userId,
    method: (config.method || "post").toLowerCase(),
    url: config.url,
    baseURL: config.baseURL,
    params: config.params,
    data: config.data,
    headers: {
      Accept: headers.Accept || headers.accept,
      "Content-Type": headers["Content-Type"] || headers["content-type"],
      "X-Idempotency-Key": clientRequestId,
    },
    status: "pending",
    attempts: 0,
    createdAt: Date.now(),
    nextAttemptAt: 0,
  };
  const id = await offlineDb.queue.add(queued);
  window.dispatchEvent(new CustomEvent("offline:queue-changed"));
  return { ...queued, id };
};

export const selectReplayableRequests = (
  items,
  { force = false, now = Date.now() } = {}
) => {
  return items.filter((item) => {
    if (item.status === "blocked") return false;
    if (
      item.status === "failed" &&
      !force &&
      item.nextAttemptAt &&
      item.nextAttemptAt > now
    ) {
      return false;
    }
    return ["pending", "syncing", "failed"].includes(item.status);
  });
};

export const getQueuedRequests = async ({ force = false } = {}) => {
  const items = await offlineDb.queue
    .where("userId")
    .equals(getCurrentUserId())
    .sortBy("createdAt");
  return selectReplayableRequests(items, { force });
};

export const getQueueCount = () =>
  offlineDb.queue
    .where("userId")
    .equals(getCurrentUserId())
    .and((item) => ["pending", "syncing", "failed", "blocked"].includes(item.status))
    .count();

export const getQueueItems = () =>
  offlineDb.queue.where("userId").equals(getCurrentUserId()).sortBy("createdAt");

export const getProjectedResponseData = async (config, data) => {
  if (!Array.isArray(data)) return data;

  const [queueItems, referenceEntries] = await Promise.all([
    getQueueItems(),
    Promise.all(
      OPTIMISTIC_REFERENCE_RESOURCES.map(async (resource) => {
        const cached = await getCachedResponse(
          { ...config, url: resource, params: {} },
          { allowExpired: true }
        );
        return [resource, cached?.data];
      })
    ),
  ]);

  return projectQueuedMutations(
    config,
    data,
    queueItems,
    Object.fromEntries(referenceEntries)
  );
};

export const removeQueuedRequest = async (id) => {
  await offlineDb.queue.delete(id);
  window.dispatchEvent(new CustomEvent("offline:queue-changed"));
};

export const getQueuedRequestRemovalImpact = async (id) =>
  getQueueRemovalImpact(await getQueueItems(), id);

export const discardQueuedRequest = async (id) => {
  const impacted = await getQueuedRequestRemovalImpact(id);
  await offlineDb.queue.bulkDelete(impacted.map((item) => item.id));
  window.dispatchEvent(new CustomEvent("offline:queue-changed"));
  return impacted;
};

export const markQueuedRequestSyncing = (id) =>
  offlineDb.queue.update(id, { status: "syncing", error: null });

export const rewriteQueuedRequestReferences = async (sourceId, mappings) => {
  if (!Object.keys(mappings).length) return 0;

  const items = await offlineDb.queue
    .where("userId")
    .equals(getCurrentUserId())
    .sortBy("createdAt");
  const sourceIndex = items.findIndex((item) => item.id === sourceId);
  const laterItems = sourceIndex >= 0 ? items.slice(sourceIndex + 1) : items;
  let updated = 0;

  await offlineDb.transaction("rw", offlineDb.queue, async () => {
    for (const item of laterItems) {
      const data = replaceOfflineReferences(item.data, mappings);
      const params = replaceOfflineReferences(item.params, mappings);
      const url = replaceOfflineReferences(item.url, mappings);
      if (data !== item.data || params !== item.params || url !== item.url) {
        const unresolved = getUnresolvedOfflineReferences({ data, params, url });
        const dependencyResolved =
          item.blockedReason === "dependency" && unresolved.length === 0;
        await offlineDb.queue.update(item.id, {
          data,
          params,
          url,
          ...(dependencyResolved
            ? {
                status: "pending",
                error: null,
                blockedReason: null,
                blockedReferences: [],
                nextAttemptAt: 0,
              }
            : {}),
        });
        updated += 1;
      }
    }
  });
  return updated;
};

export const markQueuedRequestFailed = async (id, error, { blocked = false } = {}) => {
  const request = await offlineDb.queue.get(id);
  const attempts = (request?.attempts || 0) + 1;
  const permanentlyBlocked = blocked || attempts >= 5;
  const retryDelay = Math.min(30 * 60 * 1000, 15 * 1000 * 2 ** Math.max(0, attempts - 1));

  return offlineDb.queue.update(id, {
    status: permanentlyBlocked ? "blocked" : "failed",
    error,
    attempts,
    nextAttemptAt: permanentlyBlocked ? null : Date.now() + retryDelay,
  });
};

export const markQueuedRequestWaitingForDependency = (id, references) =>
  offlineDb.queue.update(id, {
    status: "blocked",
    blockedReason: "dependency",
    blockedReferences: references,
    error:
      "En attente : une donnée liée doit d’abord être corrigée et synchronisée.",
    nextAttemptAt: null,
  });

export const retryQueuedRequest = async (id) => {
  await offlineDb.queue.update(id, {
    status: "pending",
    error: null,
    blockedReason: null,
    blockedReferences: [],
    nextAttemptAt: 0,
  });
  window.dispatchEvent(new CustomEvent("offline:queue-changed"));
};

export const clearCachedResponses = () =>
  offlineDb.responses.where("userId").equals(getCurrentUserId()).delete();

export const saveOfflinePreparation = (summary) => {
  const context = getCurrentContext();
  return offlineDb.metadata.put({
    key: createPreparationKey(),
    userId: getCurrentUserId(),
    module: context.module,
    updatedAt: Date.now(),
    ...summary,
  });
};

export const getOfflinePreparation = () =>
  offlineDb.metadata.get(createPreparationKey());

export const clearCurrentUserOfflineData = async () => {
  const userId = getCurrentUserId();
  await offlineDb.transaction(
    "rw",
    offlineDb.responses,
    offlineDb.queue,
    offlineDb.metadata,
    async () => {
      await offlineDb.responses.where("userId").equals(userId).delete();
      await offlineDb.queue.where("userId").equals(userId).delete();
      await offlineDb.metadata.where("userId").equals(userId).delete();
    }
  );
  window.dispatchEvent(new CustomEvent("offline:queue-changed"));
};
